import moment from 'moment';
import React, { useEffect, useState } from 'react';

import { Visit } from '../../store/types/visit';
import getTeleRecordingsV2 from '../../api/getTeleRecordingsV2';
import { track } from '../../tracking';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../firestore';

type VRP = {
    visit: Visit;
};

const VideoRecording: React.FC<VRP> = ({ visit }) => {
    const [fetchState, setFetchState] = useState<'loading' | 'error' | 'success' | 'empty'>(
        'loading',
    );
    const [recordingUrls, setRecordingUrls] = useState<string[]>([]);

    const doStuff = async () => {
        setFetchState('loading');
        try {
            const docRef = doc(firestore, 'appointments', visit.id, 'tele', visit.id);
            const snap = await getDoc(docRef);

            if (!snap.exists()) {
                console.warn('No Firestore doc found for this appointment');
                setFetchState('empty');
                return;
            }

            const data = snap.data();
            const recordingIds = data?.recordingIds;

            if (!Array.isArray(recordingIds) || recordingIds.length === 0) {
                setFetchState('empty');
                return;
            }

            console.log(recordingIds, 'recordingIds');

            const res = await getTeleRecordingsV2(recordingIds);

            if (res.success && res.urls && res.urls.length > 0) {
                setRecordingUrls(res.urls);
                setFetchState('success');
            } else {
                setFetchState('empty');
            }
            track('CLICKED_ON_VIEW_CALL_RECORDINGS, aid — ', {
                aid: visit.id,
            });
        } catch (e) {
            setFetchState('error');
        }
    };

    useEffect(() => {
        doStuff();
    }, [visit]);

    if (fetchState === 'loading') {
        return (
            <div className="flex h-full justify-center items-center">
                <div className="pulse">
                    <svg width="16px" height="12px">
                        <polyline id="back" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                        <polyline id="front" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                    </svg>
                </div>
            </div>
        );
    }

    if (fetchState === 'empty') {
        return (
            <div className="flex flex-col h-full justify-center items-center space-y-8 select-none">
                <div>
                    <svg viewBox="0 0 448 512" width="42" height="48" className="fill-neutral-3">
                        <path d="M400 64h-48V12c0-6.6-5.4-12-12-12h-8c-6.6 0-12 5.4-12 12v52H128V12c0-6.6-5.4-12-12-12h-8c-6.6 0-12 5.4-12 12v52H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM48 96h352c8.8 0 16 7.2 16 16v48H32v-48c0-8.8 7.2-16 16-16zm352 384H48c-8.8 0-16-7.2-16-16V192h384v272c0 8.8-7.2 16-16 16zm-105.3-95.9c4.7 4.7 4.7 12.3 0 17l-5.7 5.7c-4.7 4.7-12.3 4.7-17 0l-48-48.2-48.1 48.1c-4.7 4.7-12.3 4.7-17 0l-5.7-5.7c-4.7-4.7-4.7-12.3 0-17l48.1-48.1-48.1-48.1c-4.7-4.7-4.7-12.3 0-17l5.7-5.7c4.7-4.7 12.3-4.7 17 0l48.1 48.1 48.1-48.1c4.7-4.7 12.3-4.7 17 0l5.7 5.7c4.7 4.7 4.7 12.3 0 17L246.6 336l48.1 48.1z"></path>
                    </svg>
                </div>
                <div>
                    <p className="text-neutral-3">No recordings from this visit</p>
                </div>
            </div>
        );
    }
    if (fetchState === 'error') {
        return (
            <div className="flex h-full flex-col justify-center items-center select-none">
                <div className="w-64 h-64">
                    <svg width="42" height="42" viewBox="0 0 384 512" className="fill-neutral-3">
                        <path d="M369.9,97.9,286,14A48,48,0,0,0,252.1-.1H48A48.16,48.16,0,0,0,0,48V464a48,48,0,0,0,48,48H336a48,48,0,0,0,48-48V131.9A48.23,48.23,0,0,0,369.9,97.9Zm-22.6,22.7a15.73,15.73,0,0,1,4.2,7.4H256V32.5a15.73,15.73,0,0,1,7.4,4.2ZM336,480H48a16,16,0,0,1-16-16V48A16,16,0,0,1,48,32H224V136a23.94,23.94,0,0,0,24,24H352V464A16,16,0,0,1,336,480ZM262.71,369.05l-5.66,5.66a12,12,0,0,1-17,0L192,326.63l-48.08,48.08a12,12,0,0,1-17,0l-5.66-5.66a12,12,0,0,1,0-17L169.37,304l-48.08-48.08a12,12,0,0,1,0-17l5.66-5.66a12,12,0,0,1,17,0L192,281.37l48.08-48.08a12,12,0,0,1,17,0l5.66,5.66a12,12,0,0,1,0,17L214.63,304l48.08,48.08A12,12,0,0,1,262.71,369.05Z" />
                    </svg>
                </div>
                <p className="text-neutral-3 mt-8">Failed to load visit recording</p>
                <button className="primary-btn px-8 py-4 mt-16" onClick={doStuff}>
                    Try again
                </button>
            </div>
        );
    }

    return (
        <div>
            <p className="font-700 text-14 m-16">
                Video Call recording with{' '}
                {[visit.patient.name.f, visit.patient.name.l].filter(Boolean).join(' ')} on{' '}
                {moment(visit.createdAt).format('DD MMM YYYY, hh:mm A')}
            </p>
            {recordingUrls.map((url, index) => (
                <video key={index} className="w-full mb-4" src={url} controls></video>
            ))}
        </div>
    );
};

export default VideoRecording;
